import {
  adminTubingVars,
  defaultTubingVars,
  deleteTubings,
  ecopetrolTubingVars,
  kocTubingVars,
  restoreTubings
} from "@/models/equipment/tubings/predicates";
import type { Tubings } from "gql/api";
import {
  useTubingsQuery,
  useDeleteTubingsMutation,
  useInsertTubingMutation,
  useRestoreTubingsMutation,
  useUpdateTubingByPkMutation,
  Tubings_Type_Enum,
  useTubingsTypesQuery
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { tubingTableColumns } from "./tubingTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useTubings(
  isAdmin: boolean,
  allowedTubingTypes?: Tubings_Type_Enum[]
): UseEquipment<Tubings> {
  const baseEquipment = useEquipment<Tubings>(isAdmin);
  const title = "Tubings";
  const user = useUserInfo();

  const getVariables = () => {
    // Step 1: Select base query variables based on user role/customer type
    // Each customer type may have different WHERE clauses for data isolation:
    // - KOC/Ecopetrol customers: Filtered to their organization's tubings only
    // - Admins: Includes soft-deleted tubings (deleted_at IS NULL or NOT NULL)
    // - Default users: Standard view (active tubings only, no org filtering)
    let vars: typeof defaultTubingVars;
    if (user.is_koc_customer) vars = kocTubingVars;
    else if (user.is_ecopetrol_customer) vars = ecopetrolTubingVars;
    else if (user.is_admin) vars = adminTubingVars;
    else vars = defaultTubingVars;

    // Step 2: Optionally filter by tubing type (e.g., "bypass", "normal", "both")
    // Default behavior: if not provided or empty, return all tubing types
    if (allowedTubingTypes && allowedTubingTypes.length > 0) {
      const typeFilter = {
        tubing_type: { _in: allowedTubingTypes }
      };

      // Important: We must preserve any existing WHERE clause from the user-based vars
      // to maintain permission boundaries (e.g., customer data isolation)
      if (vars.where) {
        // Merge filters using _and to ensure BOTH conditions are satisfied:
        // Example: (customer_id = 'xyz') AND (tubing_type IN ['normal', 'bypass'])
        vars = {
          ...vars,
          where: {
            _and: [vars.where, typeFilter]
          }
        };
      } else {
        // No existing where clause, just add the type filter directly
        vars = {
          ...vars,
          where: typeFilter
        };
      }
    }
    return vars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useTubingsQuery({
    variables: getVariables()
  });

  // Fetch tubings_type enum data from database to populate dropdownOptions dynamically
  // This allows tubings_type options to be managed in the database without code changes
  // Only fetch enum data for admin users since it's only needed for the admin tubings table
  const { data: tubingsTypesData, fetching: isLoadingTubingsTypes } =
    useTubingsTypesQuery({
      pause: !isAdmin
    });

  const allRows = computed(() => data.value?.tubings as Tubings[] | undefined);

  const hashOrder: (keyof Tubings)[] = ["imperial_name", "metric_name"];

  // Transform columns with dynamic dropdownOptions and format function from enum table data
  // Maps tubings_type enum table (value, comment) to:
  // - dropdownOptions: {label: comment, value: value} for dropdown selection
  // - format function: value -> comment for display in table cells
  // Note: If enum data hasn't loaded yet, dropdownOptions will be empty and format will return raw value
  // The dropdown and format will populate once the enum query completes
  // Only apply dynamic options for admin users since enum data is only fetched for admins
  const columnsWithDynamicOptions = computed(() => {
    // If not admin, return columns as-is since we don't have enum data
    if (!isAdmin) {
      return tubingTableColumns;
    }

    const tubingsTypeEnum = tubingsTypesData.value?.tubings_type || [];

    // Create lookup map: value -> comment for efficient format function
    const enumLookup = new Map(
      tubingsTypeEnum.map(
        (item: { value: string; comment?: string | null }) => [
          item.value,
          item.comment || item.value
        ]
      )
    );

    // If enum data is still loading, show a loading state
    // Otherwise, show the enum data
    const dropdownOptions = isLoadingTubingsTypes.value
      ? [{ label: "Loading...", value: "" }]
      : tubingsTypeEnum.map(
          (item: { value: string; comment?: string | null }) => ({
            label: item.comment || item.value,
            value: item.value
          })
        );

    // Dynamic format function that uses enum table comment as display value
    // Falls back to raw value if enum data not loaded or value not found
    const formatFunction = (value: unknown) => {
      if (value == null) return "";
      const stringValue = String(value);
      return enumLookup.get(stringValue) || stringValue;
    };

    return tubingTableColumns.map(column => {
      if (column.field === "tubing_type" && column.isDropdown) {
        return {
          ...column,
          // Dynamically set dropdownOptions from enum table
          // Empty array if enum data not loaded yet - dropdown will populate when query completes
          dropdownOptions,
          // Dynamically set format function to use enum table comment (display value)
          // Falls back to raw value if enum data not loaded
          format: formatFunction
        };
      }
      return column;
    });
  });

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertTubing } = useInsertTubingMutation();
  const { executeMutation: executeUpdateTubing } =
    useUpdateTubingByPkMutation();
  const { executeMutation: executeDeleteTubing } = useDeleteTubingsMutation();
  const { executeMutation: executeRestoreTubing } = useRestoreTubingsMutation();

  const insertTubing = (tubing: Omit<Tubings, "sizing_import" | "user">) =>
    executeInsertTubing({ object: tubing });

  const updateTubing = (id: string, tubing: Tubings) =>
    executeUpdateTubing({ id, object: tubing });

  const deleteTubing = (ids: string[]) =>
    executeDeleteTubing({ where: deleteTubings(ids) });

  const restoreTubing = (ids: string[]) =>
    executeRestoreTubing({ where: restoreTubings(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "tubings",
    id: v4(),
    imperial_name: "",
    metric_name: "",
    outer_diameter_inches: null,
    interior_diameter_inches: null,
    drift_inches: null,
    coupling_outer_diameter_inches: null,
    pound_per_foot: null,
    tubing_type: Tubings_Type_Enum.Normal,
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null
  }));

  const columns = computed(() =>
    defineVisibleColumns(columnsWithDynamicOptions.value)
  );

  return {
    searchPlaceholder: "Search Tubings...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "3 1/2"`,
    columns,
    handleUpsert: defineUpsert(insertTubing, updateTubing),
    handleDelete: defineDelete(deleteTubing),
    handleRestore: defineRestore(restoreTubing),
    totalCount: computed(() => data.value?.tubings_aggregate.aggregate?.count),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      // TODO: This loses some reactivity here. The fix would be to add on the equipmentTypes.ts
      // Equipment columns for the columns to be either a ref or the original type. We need to think
      // about how best to handle this without needing to unwrap the ref everywhere or go back and
      // touch all of the other admin views to work with the new type. Open to opinions and ideas here 🙇
      columns: columnsWithDynamicOptions.value,
      globalSearchFields: [
        "metric_name",
        "imperial_name",
        "outer_diameter_inches"
      ],
      allRows,
      title
    })
  };
}
